/**
 * VoiceMicButton — small mic affordance that captures speech to text.
 *
 * Usage:
 *   <VoiceMicButton onTranscript={(text) => setName(text)} />
 *
 * Behavior:
 *   - Tap to start listening (red pulse animation)
 *   - Tap again or auto-stop on silence → invokes onTranscript with final text
 *   - If browser doesn't support speech recognition, renders disabled with tooltip
 *   - Mic permission prompt only fires on first tap (per iOS Safari guidelines)
 */
import { useEffect, useRef, useState } from "react";
import { Microphone, MicrophoneSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  VoiceSupported,
  RecorderSupported,
  listenOnce,
  recordAndTranscribe,
} from "../lib/voice";

export default function VoiceMicButton({
  onTranscript,
  onInterim,
  size = 36,
  label = "Voice input",
  testId = "voice-mic-btn",
  disabled = false,
  // iter-23b: engine="whisper" (default) uses Whisper for accuracy in noisy
  // environments; engine="browser" uses the free Web Speech API.
  engine = "whisper",
  prompt,
  language,
}) {
  const [listening, setListening] = useState(false);
  const abortRef = useRef(null);

  // Clean up if unmounted mid-listen
  useEffect(() => () => {
    if (abortRef.current) abortRef.current();
  }, []);

  const supported =
    engine === "whisper" ? RecorderSupported : VoiceSupported;

  if (!supported) {
    return (
      <button
        type="button"
        className="border-2 border-black bg-neutral-200 text-neutral-400 flex items-center justify-center"
        style={{ width: size, height: size }}
        title="Voice input not supported on this browser"
        data-testid={`${testId}-unsupported`}
        disabled
      >
        <MicrophoneSlash size={size * 0.5} weight="bold" />
      </button>
    );
  }

  const toggle = async () => {
    if (disabled) return;
    if (listening) {
      if (abortRef.current) abortRef.current();
      return;
    }
    setListening(true);
    const handle =
      engine === "whisper"
        ? recordAndTranscribe({ prompt, language })
        : listenOnce({ interim: !!onInterim, onInterim });
    abortRef.current = engine === "whisper" ? handle.stop : handle.abort;
    try {
      const text = await handle.promise;
      if (text) {
        onTranscript?.(text);
      } else {
        toast("NO SPEECH DETECTED");
      }
    } catch (err) {
      const msg = String(err?.message || err);
      if (msg.includes("not-allowed") || msg.includes("denied") || msg.includes("NotAllowed")) {
        toast.error("MIC PERMISSION DENIED · ENABLE IN BROWSER SETTINGS");
      } else if (msg.includes("503")) {
        toast.error("WHISPER UNAVAILABLE · CHECK ADMIN > BILLING");
      } else {
        toast.error(`VOICE ERROR: ${msg.toUpperCase().slice(0, 80)}`);
      }
    } finally {
      abortRef.current = null;
      setListening(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      title={label}
      aria-label={label}
      aria-pressed={listening}
      data-testid={listening ? `${testId}-listening` : testId}
      className={`border-2 border-black flex items-center justify-center transition-colors ${
        listening
          ? "bg-red-600 text-white animate-pulse"
          : "bg-white text-black hover:bg-neutral-100"
      }`}
      style={{ width: size, height: size }}
    >
      <Microphone size={size * 0.5} weight="bold" />
    </button>
  );
}
